import {gql} from '@apollo/client';
import produce from 'immer';
import React from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import DataConnector, {RefProp} from 'connect/DataConnector';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Section} from 'constants/NavConstants';
import {SchemaInterfaceExtendingSchema} from 'constants/RegistryConstants';
import {BatchSchema} from 'constants/SampleConstants';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import {ProcessClientOrigin, SearchSection} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import navigateToURL, {NavigateToURLOptions} from 'lib/navigateToURL';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {getLoadingState, LoadingState} from 'models/reduceLoadingState';
import NavStore from 'nav/NavStore';
import {SearchView} from 'nav/sidepanel/listing/search-results/SearchResultsView';
import {getSelectedPageSize, setPageSizePreference} from 'nav/sidepanel/PageSizeUtil';
import {openListing} from 'nav/sidepanel/SidepanelReduxActions';
import preferences from 'Preferences';
import {getStore} from 'redux-stores/Store';
import AssayStore from 'results/AssayStore';
import SampleSchemaStore from 'samples/SampleSchemaStore';
import StorableStore from 'samples/StorableStore';
import {mapSchemaInterfaceGraphQLToSchemaInterfaceExtendingSchema} from 'schemas/schema-interface/fields/CustomSchemaInterfaceFieldsUtils';
import {Schema} from 'schemas/SchemaConstants';
import {ConnectedSearchQuery} from 'search/__generated/ConnectedSearchQuery';
import {SavedSearchByIdQuery, SavedSearchByIdQueryVariables} from 'search/__generated/SavedSearchByIdQuery';
import {
  convertFromGraphqlSavedSearchParams,
  NAV_SECTION_TO_SAVED_SEARCH_SECTION,
  NonPaginatedSearchParams,
  SAVED_SEARCH_SECTION_TO_NAV_SECTION,
  SAVED_SEARCH_SECTION_TO_SEARCH_TYPE,
  SavedSearch,
} from 'search/saved-searches/SavedSearchUtil';
import {switchToSavedSearchProjectFolder} from 'search/saved-searches/switchToSavedSearchProjectFolder';
import {switchToSavedSearchRegistryForRegistrySidepanel} from 'search/saved-searches/switchToSavedSearchRegistryForRegistrySidepanel';
import {switchToSavedSearchRegistryForStorageSidepanel} from 'search/saved-searches/switchToSavedSearchRegistryForStorageSidepanel';
import * as SearchPreferences from 'search/SearchPreferences';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import throttle from 'util/throttle';

import {validateFilters} from './filters/ValidateFilters';
import * as FilterUtil from './FilterUtil';
import {finalizeListingSorts} from './getSortsOrDefault';
import {SearchUISelector} from './reduceSearchUI';
import {
  AsyncFetcherLocation,
  AsyncFetcherIsFetchedState,
  DEFAULT_ASYNC_FETCHER_IS_FETCHED_STATE,
  DEFAULT_LISTING_STATE,
  Filter,
  FilterErrors,
  FilterKey,
  keyToEmptyFilter,
  SearchParams,
  SearchResults,
  SearchType,
  Sort,
  UIListingState,
  SearchStatus,
} from './SearchConstants';
import * as SearchUIReduxActions from './SearchUIReduxActions';
import {sanitizeSearchParams} from './SearchUtil';

interface SchemasByType {
  [key: string]: Array<Schema>;
}

export interface SearchProps {
  // TODO(Search): Remove once all listings actions have been updated to new event handlers. This is the
  // legacy event handler that expects the caller to manage the search parameter state transitions itself.
  // New events should use the granular event handlers instead.
  onSearch: (searchParams: SearchParams) => void;
  // Granular event handlers for triggering allowed state transitions.
  onUpdateFilters: (filters: Array<Filter>) => void;
  onUpdateOffset: (offset: number) => void;
  onUpdateQueryString: (queryString: string) => void;
  onUpdateSortOrder: (sorts: Array<Sort>) => void;
  onUpdatePageSize: (limit: number) => void;
  onUpdateGroupBy: (groupField: string | null) => void;

  expandedItemIds: Array<string>;
  filterErrors: FilterErrors;
  searchParams: SearchParams;
  searchResults: SearchResults | null;
  selectedSchema: Schema | BatchSchema | null;
  selectedIds: Array<string>;
  onToggleItemsExpanded: (ids: Array<string>, isExpanded: boolean) => void;
  onRequestMoreChildItems: (parentId: string) => void;
  childrenLoadingIds: Array<string>;
  childPageSize: number | null;
  onSelectIds: (selectedIds: Array<string>) => void;
  onRefreshListing: () => void;
  loadingState: LoadingState | null;
  schemasByType: SchemasByType;

  onSelectSavedSearch: (savedSearch?: SavedSearch) => Promise<void>;
  activeSavedSearch?: SavedSearch;
  listingState: UIListingState;
  asyncFetcherIsFetchedState: AsyncFetcherIsFetchedState;
  onUpdateViewSelection: (viewSelection: SearchView) => void;
  onUpdateGroupBySelection: (groupBySelection: string | undefined) => void;
  onUpdateMultiqueryBlastOffset: (multiqueryBlastOffset: {[groupId: string]: number}) => void;
  onUpdateAsyncFetcherIsFetchedState: (flagName: AsyncFetcherLocation, value: boolean) => void;
}
interface SearchPropsWithId extends Omit<SearchProps, 'searchParams'> {
  // searchParams may be null at first, but should be set via the default params or a started search before
  // the WrappedComponent renders.
  searchParams: SearchParams | null;
  // Only used by these HOC and not passed to WrappedComponent
  searchId: string | null;
}

// To enable PureComponent optimization
const EMPTY_ITEM_IDS: Array<string> = [];
const FAILED_SEARCH_RESULTS: SearchResults = {
  searchId: 'searchId',
  status: SearchStatus.COMPLETE,
  ids: [],
  groupItemIds: [],
  total: 0,
  isExact: true,
  timedOut: false,
  nextToken: {},
};

export interface NavigationUpdate {
  url: string;
  options?: NavigateToURLOptions;
}

export const CONNECTED_SEARCH_QUERY = gql`
  query ConnectedSearchQuery @skipAudit @engComponent(name: SIGHTGLASS) {
    ...AllProcessTaskSchemasForSearchGraphQL
    ...AllStudySchemasGraphQL
    ...AllSchemaInterfacesGraphQL
    ...AllTagSchemasGraphQL
  }
  ${getFragmentWrapper('AllProcessTaskSchemasForSearch').fragment}
  ${getFragmentWrapper('AllStudySchemas').fragment}
  ${getFragmentWrapper('AllSchemaInterfaces').fragment}
  ${getFragmentWrapper('AllTagSchemas').fragment}
`;

export const SAVED_SEARCH_BY_ID_QUERY = gql`
  query SavedSearchByIdQuery($savedSearchId: StrictString!) @skipAudit @engComponent(name: SIGHTGLASS) {
    savedSearch(id: $savedSearchId) {
      ...SavedSearchGraphQL
    }
  }
  ${getFragmentWrapper('SavedSearch').fragment}
`;

/**
 * @param options.filterKeysToRemember - Settings for these filters will be automatically saved as user
 *                                       preferences. To apply the saved filters on load, callers must
 *                                       pass them in defaultSearchParams. See SearchPreferences.ts for how
 *                                       to retrieve the user's filter preferences.
 * @param options.getSearchSessionId   - If unspecified, uses the navSection as the session ID, which is
 *                                       fine for global, singleton listings. When within a workspace item,
 *                                       use a unique session ID, e.g. studyItemsListing:stdy_123.
 *                                       NOTE: This is only invoked on initial render with initial props.
 */
export default function withSearching<P extends {}, D = {}>(
  WrappedComponent: React.ComponentType<P & SearchProps>,
  searchType: SearchType,
  navSection: Section,
  {
    defaultSearchParams,
    getNavigationUpdate,
    filterKeysToRemember,
    savedSearchPreferenceKey,
    getSearchSessionId,
  }: {
    defaultSearchParams?: SearchParams;
    getNavigationUpdate?: (prevProps: P & SearchProps, props: P & SearchProps) => NavigationUpdate | null;
    filterKeysToRemember?: Array<FilterKey>;
    savedSearchPreferenceKey?: string;
    getSearchSessionId?: (initialProps: P & SearchProps) => string;
  }
): React.ComponentClass<Omit<P, keyof D>> {
  class SearchConnector extends DataConnector<
    P & {
      additionalSchemasByType?: ConnectedSearchQuery;
    },
    SearchPropsWithId
  > {
    private readonly searchSessionId;

    static defaultProps = {...WrappedComponent.defaultProps};

    override stores = [AssayStore, NavStore, SampleSchemaStore, StorableStore];

    constructor(props: P & SearchProps) {
      super(props);
      this.searchSessionId = getSearchSessionId ? getSearchSessionId(props) : navSection;
    }

    _handleRefreshListing = (): void => {
      this.dispatch(SearchUIReduxActions.refreshListing(this.searchSessionId));
    };

    _schemas(): Array<Schema | BatchSchema> {
      return Object.values(this._schemasByType()).flat().concat(this._schemaInterfaces());
    }

    _schemaInterfaces(): Array<SchemaInterfaceExtendingSchema> {
      return this.props.additionalSchemasByType?.schemaInterfaces
        ? this.props.additionalSchemasByType.schemaInterfaces.map((schemaInterface) =>
            mapSchemaInterfaceGraphQLToSchemaInterfaceExtendingSchema(schemaInterface)
          )
        : [];
    }

    _schemasByType(): SchemasByType {
      const gqlSchemasByType: SchemasByType =
        this.props.additionalSchemasByType != null
          ? {
              [ModelTypeConstants.PROCESS_OUTPUT_SCHEMA]:
                this.props.additionalSchemasByType.processTaskSchemasSupportingPlatformFeature
                  .filter(({outputSchema}) => Boolean(outputSchema))
                  .map(({name, outputSchema}) => ({...outputSchema!, name})),
              [ModelTypeConstants.PROCESS_TASK_SCHEMA]:
                this.props.additionalSchemasByType.processTaskSchemasSupportingPlatformFeature,
              [ModelTypeConstants.STUDY_SCHEMA]: this.props.additionalSchemasByType.studySchemas,
              [ModelTypeConstants.TAG_SCHEMA]: this.props.additionalSchemasByType.tagSchemas,
            }
          : {};
      return {
        [ModelTypeConstants.CONTAINER_SCHEMA]: this.sampleSchemaStore().getContainerSchemas() || [],
        [ModelTypeConstants.PLATE_SCHEMA]: this.sampleSchemaStore().getPlateSchemas() || [],
        [ModelTypeConstants.BOX_SCHEMA]: this.sampleSchemaStore().getBoxSchemas() || [],
        [ModelTypeConstants.LOCATION_SCHEMA]: this.sampleSchemaStore().getLocationSchemas() || [],
        [ModelTypeConstants.REQUEST_SCHEMA]: this.assayStore().getRequestSchemas('withSearching') || [],
        [ModelTypeConstants.RUN_SCHEMA]: this.assayStore().getRunSchemas('withSearching') || [],
        ...gqlSchemasByType,
      };
    }

    _validateFilters = (filters: Array<Filter>): FilterErrors => {
      const selectedSchema = FilterUtil.getSelectedSchema(filters, this._schemas());
      return validateFilters(filters, selectedSchema);
    };

    _handleSearch = (
      searchParams: SearchParams,
      searchSessionIdOverride?: string,
      searchTypeOverride?: SearchType
    ): void => {
      const searchSessionId = searchSessionIdOverride ?? this.searchSessionId;
      const filterErrors = this._validateFilters(searchParams.filters);
      const selector = new SearchUISelector(this.reduxState());
      const prevSessionState = selector.getSearchSessionState(searchSessionId);
      const searchTypeForAction = searchTypeOverride ?? searchType;
      if (
        prevSessionState != null &&
        isEqual(
          finalizeListingSorts(sanitizeSearchParams(searchParams, filterErrors), searchType),
          finalizeListingSorts(
            sanitizeSearchParams(prevSessionState.searchParams, prevSessionState.filterErrors),
            searchType
          )
        )
      ) {
        // Search params were the same, so just update the search params and don't kick off a search.
        this.dispatch(
          SearchUIReduxActions.updateSearchParams(
            searchSessionId,
            searchTypeForAction,
            searchParams,
            filterErrors
          )
        );
        return;
      }
      this.dispatch(
        SearchUIReduxActions.updateParamsAndSearch(
          searchSessionId,
          searchTypeForAction,
          searchParams,
          filterErrors
        )
      );
    };

    _handleUpdateFilters = (filters: Array<Filter>): void => {
      const selectedSchema = FilterUtil.getSelectedSchema(filters, this._schemas());

      this.dispatch(
        SearchUIReduxActions.updateFilters(this.searchSessionId, searchType, filters, selectedSchema)
      );
      if (filterKeysToRemember != null) {
        SearchPreferences.rememberAppliedFiltersToUserPreference(navSection, filters, filterKeysToRemember);
      }
    };

    _handleUpdateOffset = (offset: number): void => {
      this.dispatch(SearchUIReduxActions.pageChanged(this.searchSessionId, searchType, offset));
    };

    _handleUpdateQueryString = (queryString: string): void => {
      this.dispatch(SearchUIReduxActions.updateQueryString(this.searchSessionId, searchType, queryString));
    };

    _handleUpdateSortOrder = (sorts: Array<Sort>): void => {
      this.dispatch(SearchUIReduxActions.updateSortOrder(this.searchSessionId, searchType, sorts));
      SearchPreferences.rememberAppliedSortsToUserPreference(navSection, sorts);
    };

    _handleUpdatePageSize = (limit: number): void => {
      this.dispatch(SearchUIReduxActions.updatePageSize(this.searchSessionId, searchType, limit));
      setPageSizePreference(limit);
    };

    _handleUpdateGroupBy = (groupField: string | null): void => {
      this.dispatch(SearchUIReduxActions.updateGroupBy(this.searchSessionId, searchType, groupField));
    };

    _handleSelectIds = (selectedIds: Array<string>): void => {
      const searchId = this._getSearchId();
      if (searchId) {
        this.dispatch(SearchUIReduxActions.selectIds(searchId, selectedIds));
      }
    };

    _handleToggleItemsExpanded = (itemIds: Array<string>, isExpanded: boolean): void => {
      const searchId = this._getSearchId();
      if (searchId) {
        this.dispatch(SearchUIReduxActions.setItemsExpanded(searchId, itemIds, isExpanded));
      }
    };

    _handleLoadMoreChildItems = (itemId: string): void => {
      const searchId = this._getSearchId();
      if (searchId) {
        this.dispatch(SearchUIReduxActions.loadMoreChildItems(searchId, itemId));
      }
    };

    _handleUpdateViewSelection = (viewSelection: SearchView): void => {
      const searchId = this._getSearchId();
      if (searchId) {
        this.dispatch(SearchUIReduxActions.updateViewSelection(searchId, viewSelection));
      }
    };

    _handleUpdateGroupBySelection = (groupBySelection: string): void => {
      const searchId = this._getSearchId();
      if (searchId) {
        this.dispatch(SearchUIReduxActions.updateGroupBySelection(searchId, groupBySelection));
      }
    };

    _handleUpdateMultiqueryBlastOffset = (multiqueryBlastOffset: {[groupId: string]: number}): void => {
      const searchId = this._getSearchId();
      if (searchId) {
        this.dispatch(SearchUIReduxActions.updateMultiqueryBlastOffset(searchId, multiqueryBlastOffset));
      }
    };

    _handleUpdateAsyncFetcherIsFetchedState = (flagName: AsyncFetcherLocation, value: boolean): void => {
      const searchId = this._getSearchId();
      if (searchId) {
        this.dispatch(SearchUIReduxActions.updateAsyncFetcherIsFetchedState(searchId, flagName, value));
      }
    };

    _getSearchId = (): string | null => {
      const selector = new SearchUISelector(this.reduxState());
      return selector.getSearchIdForSearchSession(this.searchSessionId);
    };

    _onSelectSavedSearch = async (savedSearch?: SavedSearch): Promise<void> => {
      const addRequiredFilters = (
        searchType: SearchType,
        searchParams: NonPaginatedSearchParams
      ): NonPaginatedSearchParams =>
        produce(searchParams, (draftSearchParams) => {
          if (
            searchType === SearchType.PROCESS_MANAGEMENT &&
            !draftSearchParams.filters.some(({key}) => key === FilterKey.PROCESS_CLIENT_ORIGIN)
          ) {
            draftSearchParams.filters.push({
              ...keyToEmptyFilter(FilterKey.PROCESS_CLIENT_ORIGIN),
              value: ProcessClientOrigin.PROCESSES,
            });
          }
        });

      if (savedSearch) {
        if (savedSearch.section === SearchSection.PROJECT_ITEMS) {
          await switchToSavedSearchProjectFolder(savedSearch);
        } else if (savedSearch.section === SearchSection.REGISTRIES) {
          await switchToSavedSearchRegistryForRegistrySidepanel(savedSearch);
        } else if (savedSearch.section === SearchSection.STORAGE) {
          await switchToSavedSearchRegistryForStorageSidepanel(savedSearch);
        } else if (this.searchSessionId !== SAVED_SEARCH_SECTION_TO_NAV_SECTION[savedSearch.section]) {
          getStore().dispatch(
            openListing(SAVED_SEARCH_SECTION_TO_NAV_SECTION[savedSearch.section], {
              offset: 0,
              limit: getSelectedPageSize(),
              ...addRequiredFilters(
                searchType,
                convertFromGraphqlSavedSearchParams(savedSearch.searchParams)
              ),
            })
          );
        }
      }

      // The PROJECTS section shows saved searches that are actually for the PROJECT_ITEMS section, so we
      // need to make sure to update the PROJECT_ITEMS search session instead.
      let savedSearchSearchSessionId = this.searchSessionId;
      let savedSearchSearchType = searchType;
      if (savedSearch) {
        savedSearchSearchSessionId = SAVED_SEARCH_SECTION_TO_NAV_SECTION[savedSearch.section];
        savedSearchSearchType = SAVED_SEARCH_SECTION_TO_SEARCH_TYPE[savedSearch.section];
      }

      getStore().dispatch(
        SearchUIReduxActions.setSelectedSavedSearch(savedSearch, savedSearchSearchSessionId)
      );

      if (savedSearch) {
        const selector = new SearchUISelector(this.reduxState());
        const pastSearchParams = selector.getSearchSessionState(savedSearchSearchSessionId)?.searchParams;

        // Since you can navigate to a different section for the first time through selecting a saved search,
        // there could be no past searches yet, which means searchParams would be undefined.
        const searchParams = pastSearchParams || {offset: 0, limit: getSelectedPageSize()};

        // Note that there is a quirk/minor bug where this will result in re-executing the search on update
        // when changing the text query. This happens because changing the text query switches to a relevance
        // sort by removing the sort key(s), but the saved search save routine sanitizes this to inclue the
        // relevance sort key explicitly. This means that the params applied by the change do not match the
        // saved search version passed to this handler. Correcting for this will entail pretty broad changes
        // to how we set relevance sorts, which is a bit scattered at the moment. Since the params are
        // conceptually the same, and things like page offset are preserved, this is relatively benign
        // behavior, so we are tolerating for now until this is safer to refactor.
        // TODO(Search): Switch to always specifying the default/relevance sort key instead of passing an
        //  empty list to correct the above behavior.
        this._handleSearch(
          {
            ...searchParams, // Fill in the search parameters that are not saved in a saved search
            ...addRequiredFilters(
              savedSearchSearchType,
              convertFromGraphqlSavedSearchParams(savedSearch.searchParams)
            ),
          },
          // Override the search session ID and search type in case we are loading a saved search for a
          // different listing than where the load was triggered from, such as the PROJECTS section, which
          // loads searches for the PROJECT_ITEMS section.
          savedSearchSearchSessionId,
          savedSearchSearchType
        );
      }

      if (savedSearchPreferenceKey == null) {
        return;
      }
      if (savedSearch == null) {
        preferences.unset(savedSearchPreferenceKey);
      } else if (savedSearch.section === NAV_SECTION_TO_SAVED_SEARCH_SECTION[navSection]) {
        // only saving preference if the selected saved search is of the current section
        preferences.set({
          [savedSearchPreferenceKey]: savedSearch.id,
        });
      }
    };

    enhanceProps(): SearchPropsWithId {
      const selector = new SearchUISelector(this.reduxState());
      const sessionState = selector.getSearchSessionState(this.searchSessionId);
      const activeSavedSearch = selector.getActiveSavedSearchForSearchSession(this.searchSessionId);

      const {searchId, searchParams, filterErrors} = sessionState || {
        searchId: null,
        searchParams: null,
        filterErrors: [],
      };
      const selectedSchema = searchParams
        ? FilterUtil.getSelectedSchema(searchParams.filters, this._schemas())
        : null;
      let searchResults = null;
      let loadingState = null;
      if (searchId) {
        loadingState = getLoadingState(
          this.reduxState().models.loadingState,
          ManagedModelType.SEARCH_RESULTS,
          searchId
        );
        if (loadingState === LoadingState.FAILED) {
          searchResults = FAILED_SEARCH_RESULTS;
        } else {
          searchResults = this.getOrFetchOptional(ManagedModelType.SEARCH_RESULTS, searchId);
        }
      }

      return {
        expandedItemIds: searchId ? selector.getExpandedIdsForSearch(searchId) : EMPTY_ITEM_IDS,
        onSearch: this._handleSearch,
        onUpdateFilters: this._handleUpdateFilters,
        onUpdateOffset: this._handleUpdateOffset,
        onUpdateQueryString: this._handleUpdateQueryString,
        onUpdateSortOrder: this._handleUpdateSortOrder,
        onUpdatePageSize: this._handleUpdatePageSize,
        onUpdateGroupBy: this._handleUpdateGroupBy,
        searchId,
        searchParams,
        searchResults,
        selectedSchema,
        filterErrors,
        selectedIds: searchId ? selector.getSelectedIdsForSearch(searchId) : EMPTY_ITEM_IDS,
        onSelectIds: this._handleSelectIds,
        onToggleItemsExpanded: this._handleToggleItemsExpanded,
        onRequestMoreChildItems: this._handleLoadMoreChildItems,
        childrenLoadingIds: searchId ? selector.getChildrenLoadingIdsForSearch(searchId) : [],
        childPageSize: searchId ? selector.getChildrenPageSizeForSearch(searchId, false) : null,
        onRefreshListing: this._handleRefreshListing,
        loadingState,
        schemasByType: this._schemasByType(),
        onSelectSavedSearch: this._onSelectSavedSearch,
        activeSavedSearch,
        listingState: searchId ? selector.getListingStateForSearch(searchId) : DEFAULT_LISTING_STATE,
        asyncFetcherIsFetchedState: searchId
          ? selector.getAsyncFetcherIsFetchedStateForSearch(searchId)
          : DEFAULT_ASYNC_FETCHER_IS_FETCHED_STATE,
        onUpdateViewSelection: this._handleUpdateViewSelection,
        onUpdateGroupBySelection: this._handleUpdateGroupBySelection,
        onUpdateMultiqueryBlastOffset: this._handleUpdateMultiqueryBlastOffset,
        onUpdateAsyncFetcherIsFetchedState: this._handleUpdateAsyncFetcherIsFetchedState,
      };
    }
  }

  type Props = P & SearchPropsWithId & RefProp;
  class SearchComponent extends React.Component<Props> {
    _throttledRefreshResults: () => void;
    _isMounted: boolean = false;

    constructor(props: Props) {
      super(props);
      // If a search hasn't already been launched and there are default params, kick off an initial search.
      // We also check for a saved search preference and auto-load that, but we can only do this if the
      // defaultSearchParams are present, since otherwise the search param sanitization process that runs
      // as part of loading a saved search won't have current params to combine with to fill in the bits
      // not saved with the search.
      if (!props.searchId && defaultSearchParams) {
        // This needs to happen first even if we immediately load a saved search so that we set the initial
        // params.
        props.onSearch(defaultSearchParams);

        const defaultToSavedSearch =
          savedSearchPreferenceKey != null && preferences.get(savedSearchPreferenceKey);

        if (defaultToSavedSearch) {
          this._loadSavedSearchFromPreference(preferences.get(savedSearchPreferenceKey));
        }
      }
      this._throttledRefreshResults = throttle(() => {
        this._refreshResults();
      }, 2000);
    }

    override componentDidMount(): void {
      this._isMounted = true;
      FolderDAOCache.on(FolderDAOCache.FOLDER_CACHE_INVALIDATE, this._handleFolderDAOCacheInvalidate);
    }

    async _loadSavedSearchFromPreference(savedSearchId: string | null): Promise<void> {
      if (!savedSearchId) {
        return;
      }
      const savedSearchResult = await doQuerySafe<SavedSearchByIdQuery, SavedSearchByIdQueryVariables>(
        {query: SAVED_SEARCH_BY_ID_QUERY},
        {variables: {savedSearchId}}
      );
      if (
        this._isMounted &&
        savedSearchResult.savedSearch &&
        !isInaccessible(savedSearchResult.savedSearch) &&
        // it shouldn't be possible to save a search from a different section in preference anymore, but adding
        // this for existing preferences that still have "incorrect" data.
        (savedSearchResult.savedSearch as SavedSearch).section ===
          NAV_SECTION_TO_SAVED_SEARCH_SECTION[navSection]
      ) {
        await this.props.onSelectSavedSearch(savedSearchResult.savedSearch as SavedSearch);
      }
    }

    override componentWillUnmount(): void {
      this._isMounted = false;
      FolderDAOCache.off(FolderDAOCache.FOLDER_CACHE_INVALIDATE, this._handleFolderDAOCacheInvalidate);
    }

    override shouldComponentUpdate(newProps: Props): boolean {
      // props.schemasByType is an object and so needs a deep equality check to prevent the component
      // from re-rendering
      return !isEqual(newProps, this.props);
    }

    override componentDidUpdate(prevProps: Props): void {
      if (this.props.searchParams != null && getNavigationUpdate) {
        // eslint-disable-next-line no-restricted-syntax
        const update = getNavigationUpdate(_.omit(prevProps, 'searchId'), _.omit(this.props, 'searchId'));
        if (update) {
          navigateToURL(update.url, {
            replace: false,
            skipTrigger: true,
            ...update.options,
          });
        }
      }
    }

    _refreshResults(): void {
      // Defer the execution of the action in case this code is run in the middle of dispatch
      // to avoid dispatching another event in the middle of a dispatch. Otherwise the initial
      // event isn't propagated correctly to the rest of subscribers.
      _.defer(() => {
        if (this._isMounted) {
          this.props.onRefreshListing();
        }
      });
    }

    _handleFolderDAOCacheInvalidate = (id: string): void => {
      if (this.props.searchParams == null) {
        return;
      }
      const listedId = FilterUtil.getFilterValueByKey(this.props.searchParams.filters, FilterKey.FOLDER);
      // If we aren't in a listing or we are listing the folder that was invalidated, we should refresh.
      if (!listedId || listedId === id) {
        this._throttledRefreshResults();
      }
    };

    override render(): JSX.Element {
      // searchParams should either be set by a started search or the default params. They cannot be unset
      // after.
      if (this.props.searchParams == null) {
        return <CenteredHorizontalThrobber />;
      }
      // eslint-disable-next-line no-restricted-syntax
      return <WrappedComponent {..._.omit(this.props, 'searchId')} />;
    }
  }

  const ConnectedCls = SearchConnector.connect<P, SearchPropsWithId>(SearchComponent, {
    throttleUpdates: true,
    shouldPropagateNextState: (a, b) => !isEqual(a, b),
  });

  // eslint-disable-next-line react/prefer-stateless-function
  class SearchComponentWithGql extends React.Component<Props> {
    override render(): JSX.Element {
      return (
        <BenchlingQuery<ConnectedSearchQuery>
          query={CONNECTED_SEARCH_QUERY}
          renderLoading={() => <ConnectedCls {...this.props} />}
        >
          {(searchQueryData) => <ConnectedCls {...this.props} additionalSchemasByType={searchQueryData} />}
        </BenchlingQuery>
      );
    }
  }

  return SearchComponentWithGql;
}
